import { canPromoteInPlace } from "./fundedLineage";
import React, { useState, useMemo, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { normalizeSymbol } from "../shared/utils";
import { CURRENCY } from "../shared/currency";
import { useParams, useNavigate } from "react-router-dom";
import RuleUpdateBanner from "./components/RuleUpdateBanner";
import NoRulesBanner from "./components/NoRulesBanner";
import { resolveEffectiveTierRules } from "./effectiveTierRules";
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";
import { useAuth } from "wasp/client/auth";
import {
  getAccountById,
  createPayout,
  deletePayout,
  updateAccount,
  createAccountAttachmentUploadUrl,
  finalizeAccountAttachment,
  getPayoutSuggestions,
  getCashMovementQuestions,
  acceptPayoutSuggestion,
  dismissPayoutSuggestion,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { PaymentPlanId, SubscriptionStatus } from "../payment/plans";
import MetaApiSyncStatus from "./MetaApiSyncStatus";
import TradovateSyncStatus from "./tradovate/TradovateSyncStatus";
import CashMovementQuestionCard from "./CashMovementQuestionCard";
import MetaApiConnectModal from "./MetaApiConnectModal";
import { METATRADER_SYNC_CONNECT_ENABLED } from "./metaApiFeatureFlag";
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Plus,
  Trash2,
  Pencil,
  Power,
  Upload,
  X,
  Clock,
  CalendarDays,
} from "lucide-react";
import { Button } from "../client/components/ui/button";
import PromoteToFundedModal from "./PromoteToFundedModal";
import { deactivationBadge, deactivationLabels } from "./deactivationBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../client/components/ui/card";
import { Badge } from "../client/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { AttachmentsSection } from "../account-attachments/components/AttachmentsSection";
import AccountSourceBadge from "./AccountSourceBadge";
import PredecessorLinkCard from "./components/PredecessorLinkCard";
import AccountResetSuggestionBanner from "./AccountResetSuggestionBanner";
import BalanceFreshnessHint from "./BalanceFreshnessHint";
import BalanceGapHint from "./BalanceGapHint";
import { resolveBounds, resolveFloor, type FloorInput } from "./accountProgressGeometry";
import { AccountProgressBar, type AxisTone } from "./components/AccountProgressBar";
import { buildSparklineGeometry, SPARKLINE_WIDTH, SPARKLINE_PAD } from "./equitySparklineGeometry";
import { isPropFirmFundedStatus } from "./accountStatus";
import PayoutAmountFields from "./PayoutAmountFields";
import EditPayoutDialog, { type EditablePayout } from "./EditPayoutDialog";
import { EMPTY_PAYOUT_VALUES, resolvePayoutForm, type PayoutFormValues } from "./payoutForm";

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmt(value: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtPct(value: number) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

// ─── Stat card ───────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Card variant="accent">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className="text-xl font-bold font-mono">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Une regle du palier ─────────────────────────────────────────────────────

// Le defaut corrige ici : la page affichait d'un cote des barres de
// progression et de l'autre un tableau « Regles du tier » qui repetait les
// MEMES chiffres en texte, sans lien visible entre les deux. On fusionne, et
// on distingue ce qui se MESURE (une jauge) de ce qui se DECLARE (une valeur).
function RuleRow({
  label,
  value,
  ratio,
  tone = "normal",
}: {
  label: string;
  value: string;
  /** Fraction [0, 1]. Absente pour une regle purement declarative. */
  ratio?: number;
  tone?: "normal" | "warning" | "violation";
}) {
  const barClass =
    tone === "violation"
      ? "bg-destructive"
      : tone === "warning"
        ? "bg-warning"
        : "bg-primary";

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-medium">{value}</span>
      </div>
      {ratio !== undefined && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barClass}`}
            style={{ width: `${Math.min(100, Math.max(0, ratio * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ─── PropFirm progress section ───────────────────────────────────────────────

function PropFirmSection({
  account,
  t,
}: {
  account: any;
  t: ReturnType<typeof useTranslation>;
}) {
  const { challengeProgress, bufferStatus, payoutProgress, activeTier, effectiveTemplate } = account;

  if (!effectiveTemplate || !activeTier) return null;

  const isChallenge = account.status === "challenge";
  const isFunded = isPropFirmFundedStatus(account.status, !!effectiveTemplate);

  const phase = account.resolvedPhase;
  // Résolution partagée avec la page Lite (effectiveTierRules.ts).
  const rules = resolveEffectiveTierRules(account)!;
  const effectiveProfitTargetPercent = rules.profitTargetPercent;
  const effectiveProfitTargetAmount = rules.profitTargetAmount;
  const effectiveMaxDailyLossAmount = rules.maxDailyLossAmount;
  const effectiveMaxDailyLossPercent = rules.maxDailyLossPercent;
  const effectiveMaxTotalLossAmount = rules.maxTotalLossAmount;
  const effectiveMaxTotalLossPercent = rules.maxTotalLossPercent;
  const effectiveTrailingDrawdown = rules.trailingDrawdown;
  const effectiveMinTradingDays = rules.minTradingDays;
  const effectivePayoutThreshold = rules.payoutThreshold;
  const effectivePayoutCooldown = rules.payoutCooldownDays;
  const effectiveConsistencyRule = rules.consistencyRulePercent;
  const effectiveMaxContracts = rules.maxContracts;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: effectiveTemplate.color }}
          />
          <CardTitle className="text-base">{effectiveTemplate.name}</CardTitle>
          <Badge variant="outline" className="text-xs ml-auto">
            {activeTier.label}
          </Badge>
          {account.resolvedPhase && (
            <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
              {account.resolvedPhase.name}
            </span>
          )}
        </div>
        {/* Regles figees : rend visible que ce compte ne suit plus le
            referentiel partage, et depuis quand. */}
        {account.ruleSource === "snapshot" && account.ruleSnapshotAt && (
          <p className="text-xs text-muted-foreground mt-1">
            {t.tradingAccounts.rulesFrozenBadge} ·{" "}
            {new Date(account.ruleSnapshotAt).toLocaleDateString()}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Bandeaux d'etat : ils qualifient la progression, ils ne la repetent pas. */}
        {isChallenge && challengeProgress?.isComplete && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">{t.tradingAccounts.challengeComplete}</span>
          </div>
        )}

        {/* ── Ce qui se MESURE ── */}
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            {t.tradingAccounts.rulesMeasured}
          </p>

          {isChallenge && challengeProgress && (challengeProgress.targetAmount !== null || challengeProgress.targetPercent !== null) && (
            <RuleRow
              label={t.tradingAccounts.profitTarget}
              value={
                challengeProgress.targetAmount !== null
                  ? `${fmt(challengeProgress.profit, CURRENCY)} / ${fmt(challengeProgress.targetAmount, CURRENCY)}`
                  : `${fmtPct(challengeProgress.profitPercent)} / ${challengeProgress.targetPercent}%`
              }
              ratio={challengeProgress.ratio}
            />
          )}

          {challengeProgress && challengeProgress.daysTraded > 0 && (
            <RuleRow
              label={t.tradingAccounts.tradingDays}
              value={
                challengeProgress.minTradingDays !== null
                  ? `${challengeProgress.daysTraded} / ${challengeProgress.minTradingDays}`
                  : t.tradingAccounts.daysTradedCount.replace("{count}", String(challengeProgress.daysTraded))
              }
              ratio={challengeProgress.minTradingDays !== null ? challengeProgress.daysRatio : undefined}
            />
          )}

          {bufferStatus && bufferStatus.ruleType !== null && (
            <RuleRow
              label={t.tradingAccounts.bufferDrawdown}
              value={`${fmt(bufferStatus.remainingRoom, CURRENCY)} ${t.tradingAccounts.bufferRemaining}`}
              ratio={bufferStatus.usedRatio}
              tone={bufferStatus.isViolation ? "violation" : bufferStatus.isWarning ? "warning" : "normal"}
            />
          )}

          {/* Le depassement et l'alerte ne se lisent pas dans la jauge seule :
              la couleur dit « attention », la phrase dit quoi faire. */}
          {bufferStatus && bufferStatus.ruleType !== null && bufferStatus.isViolation && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 text-destructive text-xs">
              <AlertTriangle className="w-3 h-3" />
              {t.tradingAccounts.drawdownViolation}
            </div>
          )}
          {bufferStatus && bufferStatus.ruleType !== null && bufferStatus.isWarning && !bufferStatus.isViolation && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-warning/10 text-warning text-xs">
              <AlertTriangle className="w-3 h-3" />
              {t.tradingAccounts.drawdownWarning
                .replace("{percent}", Math.round(bufferStatus.usedRatio * 100).toString())}
            </div>
          )}

          {isFunded && payoutProgress && payoutProgress.targetAmount !== null && (
            <RuleRow
              label={t.tradingAccounts.payoutTarget}
              value={`${fmt(payoutProgress.currentProfit, CURRENCY)} / ${fmt(payoutProgress.targetAmount, CURRENCY)}`}
              ratio={payoutProgress.ratio}
            />
          )}
          {isFunded && payoutProgress && payoutProgress.targetAmount !== null && payoutProgress.isReached && (
            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
              {t.tradingAccounts.payoutReached}
            </p>
          )}

          {/* Jours qualifiants : mesure distincte de l'objectif payout, c'est
              elle qui debloque le retrait chez les firmes qui l'exigent. */}
          {account.qualifyingDays != null && phase?.minWinningDaysForPayout != null && phase?.minWinningDayProfit != null && (
            <RuleRow
              label={`${t.tradingAccounts.qualifyingDaysLabel} (${fmt(phase.minWinningDayProfit, CURRENCY)}+)`}
              value={`${account.qualifyingDays} / ${phase.minWinningDaysForPayout}`}
              ratio={account.qualifyingDays / phase.minWinningDaysForPayout}
            />
          )}
          {account.qualifyingDays != null && phase?.minWinningDaysForPayout != null && phase?.minWinningDayProfit != null && account.qualifyingDays >= phase.minWinningDaysForPayout && (
            <p className="text-xs text-primary flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> {t.tradingAccounts.payoutEligible}
            </p>
          )}
        </div>

        {/* ── Ce qui se DECLARE ── */}
        <div className="space-y-3 pt-1 border-t border-border/50">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold pt-3">
            {t.tradingAccounts.rulesDeclared}
          </p>

          {/* L'objectif de profit n'est MESURE que sur un challenge. Sur un
              compte funded il reste une contrainte declaree du palier : on le
              perdrait en le rangeant uniquement au-dessus. */}
          {!isChallenge && (effectiveProfitTargetAmount !== null || effectiveProfitTargetPercent !== null) && (
            <RuleRow
              label={t.tradingAccounts.profitTarget}
              value={fmt(
                effectiveProfitTargetAmount !== null
                  ? effectiveProfitTargetAmount
                  : account.initialBalance * effectiveProfitTargetPercent! / 100,
                CURRENCY,
              )}
            />
          )}
          {/* Montant prioritaire, repli sur le pourcentage : la moitie des
              paliers reels ne renseignent que l'un des deux. */}
          {(effectiveMaxDailyLossAmount !== null || effectiveMaxDailyLossPercent !== null) && (
            <RuleRow
              label={t.tradingAccounts.maxDailyLoss}
              value={fmt(
                effectiveMaxDailyLossAmount !== null
                  ? effectiveMaxDailyLossAmount
                  : account.initialBalance * effectiveMaxDailyLossPercent! / 100,
                CURRENCY,
              )}
            />
          )}
          {(effectiveMaxTotalLossAmount !== null || effectiveMaxTotalLossPercent !== null) && (
            <RuleRow
              label={t.tradingAccounts.maxTotalLoss}
              value={fmt(
                effectiveMaxTotalLossAmount !== null
                  ? effectiveMaxTotalLossAmount
                  : account.initialBalance * effectiveMaxTotalLossPercent! / 100,
                CURRENCY,
              )}
            />
          )}
          {effectiveTrailingDrawdown !== null && (
            <RuleRow
              label={t.tradingAccounts.trailingDrawdown}
              value={fmt(effectiveTrailingDrawdown, CURRENCY)}
            />
          )}
          {effectiveMinTradingDays !== null && (
            <RuleRow
              label={t.tradingAccounts.minTradingDays}
              value={t.tradingAccounts.daysCountShort.replace("{count}", String(effectiveMinTradingDays))}
            />
          )}
          {effectivePayoutThreshold !== null && (
            <RuleRow
              label={t.tradingAccounts.payoutThreshold}
              value={fmt(effectivePayoutThreshold, CURRENCY)}
            />
          )}
          {effectivePayoutCooldown !== null && (
            <RuleRow
              label={t.tradingAccounts.payoutCooldown}
              value={t.tradingAccounts.daysCountShort.replace("{count}", String(effectivePayoutCooldown))}
            />
          )}
          {effectiveConsistencyRule !== null && (
            <RuleRow
              label={t.tradingAccounts.consistencyRule}
              value={t.tradingAccounts.consistencyRuleValue.replace("{percent}", String(effectiveConsistencyRule))}
            />
          )}
          {effectiveMaxContracts !== null && (
            <RuleRow
              label={t.tradingAccounts.maxContracts}
              value={String(effectiveMaxContracts)}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Payout section ──────────────────────────────────────────────────────────

export function PayoutsSection({
  account,
  t,
  onPayoutAdded,
  onPayoutDeleted,
}: {
  account: any;
  t: ReturnType<typeof useTranslation>;
  onPayoutAdded: () => void;
  onPayoutDeleted: () => void;
}) {
  const { toast } = useToast();
  const createPayoutFn = useAction(createPayout);
  const deletePayoutFn = useAction(deletePayout);

  const [showAddForm, setShowAddForm] = useState(false);
  const [payoutValues, setPayoutValues] = useState<PayoutFormValues>(EMPTY_PAYOUT_VALUES);
  const [editingPayout, setEditingPayout] = useState<EditablePayout | null>(null);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [certFile, setCertFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: payoutSuggestions = [], refetch: refetchSuggestions } = useQuery(
    getPayoutSuggestions,
    { accountId: account.id },
  );
  // Questions sur un mouvement inexplique. Distinctes des suggestions : une
  // suggestion AFFIRME (« c'est un retrait de X », le cash log l'a qualifie),
  // une question DEMANDE (six causes produisent le meme signal).
  const { data: cashQuestions = [], refetch: refetchCashQuestions } = useQuery(
    getCashMovementQuestions,
    { accountId: account.id },
  );
  // Suggestion being confirmed through the (prefilled) add form; flipped to
  // "accepted" only after createPayout succeeds.
  const [pendingSuggestionId, setPendingSuggestionId] = useState<string | null>(null);

  const prefillFromSuggestion = (s: any) => {
    setShowAddForm(true);
    // La baisse de solde détectée est le BRUT. Round for display: broker
    // floats can carry 1e-12 noise.
    setPayoutValues({ gross: String(Math.round(s.amount * 100) / 100), net: "" });
    setDate(new Date(s.occurredAt).toISOString().split("T")[0]);
    setPendingSuggestionId(s.id);
  };

  const handleDismissSuggestion = async (suggestionId: string) => {
    try {
      await dismissPayoutSuggestion({ suggestionId });
      refetchSuggestions();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    }
  };

  const handleAdd = async () => {
    // `amount` = NET reçu (gains), `grossAmount` = BRUT débité du compte
    // (solde, drawdown). Voir payoutForm.ts.
    const resolved = resolvePayoutForm(payoutValues);
    if (!resolved.ok) {
      if (resolved.reason === "gross_below_net") {
        toast({ title: t.tradingAccounts.payoutGrossBelowNet, variant: "destructive" });
      }
      return;
    }

    // Client-side cert validation
    if (certFile) {
      const allowed = ["application/pdf", "image/png", "image/jpeg"];
      if (!allowed.includes(certFile.type)) {
        toast({ title: t.attachments.errorMimeRejected, variant: "destructive" });
        return;
      }
      if (certFile.size > 5 * 1024 * 1024) {
        toast({ title: t.attachments.errorTooLarge, variant: "destructive" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const created = await createPayoutFn({
        accountId: account.id,
        amount: resolved.amount,
        grossAmount: resolved.grossAmount,
        date,
        notes: notes || undefined,
      });

      if (certFile) {
        try {
          const { s3UploadUrl, s3UploadFields, s3Key } =
            await createAccountAttachmentUploadUrl({
              accountId: account.id,
              fileName: certFile.name,
              mimeType: certFile.type as any,
            });
          const fd = new FormData();
          Object.entries(s3UploadFields).forEach(([k, v]) => fd.append(k, v as string));
          fd.append("file", certFile);
          const s3Res = await fetch(s3UploadUrl, { method: "POST", body: fd });
          if (!s3Res.ok) throw new Error("s3 upload failed");

          await finalizeAccountAttachment({
            accountId: account.id,
            payoutId: created.id,
            type: "payout",
            name: certFile.name,
            s3Key,
            mimeType: certFile.type as any,
          });
          toast({ title: t.tradingAccounts.payoutAdded });
        } catch (err) {
          console.error("Cert upload failed", err);
          toast({
            title: t.tradingAccounts.payoutAddedNoCert,
            variant: "destructive",
          });
        }
      } else {
        toast({ title: t.tradingAccounts.payoutAdded });
      }

      setPayoutValues(EMPTY_PAYOUT_VALUES);
      setNotes("");
      setCertFile(null);
      setShowAddForm(false);
      if (pendingSuggestionId) {
        try {
          await acceptPayoutSuggestion({ suggestionId: pendingSuggestionId });
        } catch (err) {
          console.error("accept payout suggestion failed", err);
        }
        setPendingSuggestionId(null);
        refetchSuggestions();
      }
      onPayoutAdded();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (payoutId: string) => {
    if (!window.confirm(t.tradingAccounts.payoutDeleteConfirm)) return;
    try {
      await deletePayoutFn({ id: payoutId });
      toast({ title: t.tradingAccounts.payoutDeleted });
      onPayoutDeleted();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t.tradingAccounts.payoutsHistory}</CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t.tradingAccounts.totalPayoutsReceived}: <span className="font-mono font-medium text-foreground">{fmt(account.totalPayouts ?? 0, CURRENCY)}</span>
            </span>
            <Button size="sm" variant="outline" onClick={() => setShowAddForm(true)}>
              <Plus className="w-3 h-3 mr-1" />
              {t.tradingAccounts.addPayout}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {(cashQuestions as any[]).map((q) => (
          <CashMovementQuestionCard
            key={q.id}
            question={q}
            onSettled={() => {
              refetchCashQuestions();
              // Une reponse « retrait » cree une PayoutSuggestion : la liste
              // des suggestions doit se rafraichir aussi, sinon l'utilisateur
              // ne verrait rien se passer.
              refetchSuggestions();
            }}
          />
        ))}
        {(payoutSuggestions as any[]).map((s) => (
          <div
            key={s.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3"
          >
            <div className="text-sm">
              <span className="font-medium">{t.tradingAccounts.payoutSuggestionLabel}</span>{" "}
              <span className="font-mono">{fmt(s.amount, CURRENCY)}</span>{" "}
              <span className="text-xs text-muted-foreground">
                {new Date(s.occurredAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => prefillFromSuggestion(s)}>
                {t.tradingAccounts.payoutSuggestionCreate}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleDismissSuggestion(s.id)}>
                {t.tradingAccounts.payoutSuggestionDismiss}
              </Button>
            </div>
          </div>
        ))}
        {showAddForm && (
          <div className="p-3 rounded-lg border border-border bg-muted/30 space-y-3">
            <PayoutAmountFields values={payoutValues} onChange={setPayoutValues} idPrefix="detail-payout" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t.tradingAccounts.payoutDate}</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t.tradingAccounts.payoutNotes}</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">
                {t.tradingAccounts.payoutCertificate}
              </label>
              {certFile ? (
                <div className="h-9 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm">
                  <span className="truncate" title={certFile.name}>{certFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setCertFile(null)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={t.tradingAccounts.cancel}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="h-9 flex items-center justify-center gap-2 rounded-md border border-dashed border-input bg-background px-3 text-sm text-muted-foreground hover:bg-muted cursor-pointer transition-colors">
                  <Upload className="h-4 w-4" />
                  <span>{t.propFirmManager.addPayoutCertificatePick}</span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowAddForm(false);
                  // Drop any suggestion linkage: a later MANUAL payout must not
                  // silently mark a stale suggestion as accepted.
                  setPendingSuggestionId(null);
                }}
              >
                {t.tradingAccounts.cancel}
              </Button>
              <Button size="sm" onClick={handleAdd} disabled={isSubmitting}>
                {t.tradingAccounts.addPayout}
              </Button>
            </div>
          </div>
        )}

        {!account.payouts?.length && (
          <p className="text-sm text-muted-foreground text-center py-4">{t.tradingAccounts.noPayouts}</p>
        )}

        {account.payouts?.map((p: any) => (
          <div key={p.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
            <div>
              <p className="font-mono font-medium text-sm">{fmt(p.amount, CURRENCY)}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(p.date).toLocaleDateString()}
                {p.grossAmount != null && (
                  <span className="font-mono"> · {t.tradingAccounts.payoutGrossShort.replace("{amount}", fmt(p.grossAmount, CURRENCY))}</span>
                )}
                {p.notes && ` · ${p.notes}`}
              </p>
            </div>
            <div className="flex items-center">
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setEditingPayout(p)}
                aria-label={t.tradingAccounts.payoutEdit}
                title={t.tradingAccounts.payoutEdit}
              >
                <Pencil className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => handleDelete(p.id)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        <EditPayoutDialog payout={editingPayout} onClose={() => setEditingPayout(null)} onSaved={onPayoutAdded} />
      </CardContent>
    </Card>
  );
}

// ─── Symbol stats table ───────────────────────────────────────────────────────

function SymbolStatsTable({ symbolStats, currency, t }: { symbolStats: any; currency: string; t: ReturnType<typeof useTranslation> }) {
  if (!symbolStats) return null;
  const entries = Object.entries(symbolStats as Record<string, { pnl: number; count: number; wins: number }>)
    .sort((a, b) => Math.abs(b[1].pnl) - Math.abs(a[1].pnl))
    .slice(0, 10);

  if (!entries.length) {
    return <p className="text-sm text-muted-foreground">{t.tradingAccounts.noSymbolStats}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/50">
            <th className="text-left text-muted-foreground font-normal py-2 pr-4">{t.tradingAccounts.symbol}</th>
            <th className="text-right text-muted-foreground font-normal py-2 pr-4">{t.tradingAccounts.trades}</th>
            <th className="text-right text-muted-foreground font-normal py-2 pr-4">{t.tradingAccounts.winRate}</th>
            <th className="text-right text-muted-foreground font-normal py-2">{t.tradingAccounts.pnl}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([symbol, stats]) => (
            <tr key={symbol} className="border-b border-border/30 last:border-0">
              <td className="py-2 pr-4 font-medium">{normalizeSymbol(symbol)}</td>
              <td className="py-2 pr-4 text-right font-mono">{stats.count}</td>
              <td className="py-2 pr-4 text-right font-mono">
                {stats.count > 0 ? `${((stats.wins / stats.count) * 100).toFixed(0)}%` : "—"}
              </td>
              <td className={`py-2 text-right font-mono ${stats.pnl >= 0 ? "text-pnlPositive" : "text-destructive"}`}>
                {fmt(stats.pnl, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Equity curve (simple SVG sparkline) ─────────────────────────────────────

function EquitySparkline({ equityCurve, initialBalance, bufferStatus }: {
  equityCurve: any[];
  initialBalance: number;
  bufferStatus?: FloorInput | null;
}) {
  // AVANT le retour anticipé : un hook appelé conditionnellement casserait
  // l'ordre des hooks au premier rendu sans courbe.
  const t = useTranslation();

  if (!equityCurve?.length) return null;

  // Même plancher que l'axe de progression, lu dans le MOTEUR. Jamais
  // `highWaterMark - maxAllowedLoss` : ce calcul ignore le gel (drawdownLock.ts).
  // Cas réel (LucidFlex 50k, 2026-10-19) : plancher figé à 50 100 au payout,
  // pic à 51 715, la courbe traçait 49 715 et le compte, cramé à 50 091,
  // paraissait vivant.
  const drawdownFloor = resolveFloor(bufferStatus, initialBalance);

  const balances = equityCurve.map((p) => p.balance);

  // Géométrie déportée dans un module pur (testé) : la série à UN SEUL POINT y
  // devient un segment horizontal, sans quoi `<polyline>` ne trace rien.
  const { points, floorY, labelBelow, height: h } = buildSparklineGeometry({
    balances,
    initialBalance,
    drawdownFloor,
  });

  const w = SPARKLINE_WIDTH;
  const pad = SPARKLINE_PAD;

  const isPositive = balances[balances.length - 1] >= initialBalance;
  const strokeColor = isPositive ? "hsl(var(--edgely-pnl-positive))" : "hsl(var(--edgely-pnl-negative))";

  return (
    // `relative` : ancre le libellé du plancher, rendu en HTML par-dessus le
    // SVG (voir plus bas pourquoi il ne peut pas y vivre).
    <div className="relative w-full overflow-hidden rounded-lg bg-muted/20 border border-border/30">
      {/*
        `preserveAspectRatio="none"` est VOULU pour la courbe : elle doit
        remplir la largeur disponible quel que soit le nombre de points. Mais il
        déforme tout ce que le SVG contient, et un `<text>` y devenait illisible
        (étiré horizontalement, écrasé verticalement) — les « écritures
        aplaties ». Le libellé est donc sorti du SVG et positionné en HTML, où
        il garde la typographie de l'app et reste traduit.
      */}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-28" preserveAspectRatio="none">
        {floorY !== null && (
          <line
            x1={pad} y1={floorY} x2={w - pad} y2={floorY}
            stroke="hsl(var(--edgely-pnl-negative))"
            strokeWidth="1.5"
            strokeDasharray="6 4"
            opacity="0.85"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          points={points.join(" ")}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {floorY !== null && (
        // `floorY / h` reste une proportion valide malgré la déformation :
        // seule la géométrie interne du SVG est étirée, pas la hauteur de la
        // boîte. Le libellé est posé JUSTE AU-DESSUS de la ligne, et bascule
        // en dessous quand celle-ci est trop près du bord haut.
        <span
          className="pointer-events-none absolute right-2 text-[10px] font-medium leading-none text-pnlNegative/90"
          style={
            labelBelow
              ? { top: `calc(${(floorY / h) * 100}% + 4px)` }
              : { bottom: `calc(${(1 - floorY / h) * 100}% + 4px)` }
          }
        >
          {t.tradingAccounts.equityCurveMaxLossLimit}
        </span>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AccountDetailPage() {
  const t = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const metricsAnimKey = `account-${accountId}`;
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [showConnectModal, setShowConnectModal] = useState(false);

  const isFreePlan = useMemo(() => {
    if (!user) return false;
    if (!user.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!user.subscriptionStatus || user.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [user]);
  const isPro = !isFreePlan;
  const [celebratePayout, setCelebratePayout] = useState(false);
  const payoutCardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const updateAccountFn = useAction(updateAccount);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);

  const { data: account, isLoading, refetch } = useQuery(
    getAccountById,
    { id: accountId ?? "" },
    { enabled: !!accountId },
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!account) return;
    try {
      await updateAccountFn({
        id: account.id,
        isActive: false,
        disabledAt: (account as any).disabledAt ? undefined : new Date().toISOString(),
      });
      toast({ title: t.tradingAccounts.accountDeactivated });
      setShowDeactivateConfirm(false);
      refetch();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    }
  }, [account, updateAccountFn, toast, t, refetch]);

  const handleReactivate = useCallback(async () => {
    if (!account) return;
    try {
      await updateAccountFn({ id: account.id, isActive: true, disabledAt: null });
      toast({ title: t.tradingAccounts.accountActivated });
      refetch();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    }
  }, [account, updateAccountFn, toast, t, refetch]);

  const handlePayoutAdded = useCallback(() => {
    setCelebratePayout(true);
    setTimeout(() => setCelebratePayout(false), 1500);
    refetch();
  }, [refetch]);

  const handlePayoutDeleted = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground mb-4">{t.tradingAccounts.loading}</p>
        <Button variant="outline" onClick={() => navigate("/trading-accounts")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.tradingAccounts.backToAccounts}
        </Button>
      </div>
    );
  }

  // Variation en pourcentage du PnL des trades face au solde initial. Depuis
  // que l'en-tête affiche le profit de l'OBJECTIF, elle n'est plus lue là-haut :
  // elle sert désormais de sous-titre à la tuile « PnL des trades » (étage 3).
  const variation = account.initialBalance > 0
    ? ((account.totalPnl / account.initialBalance) * 100)
    : 0;

  // LE CHIFFRE DE RÉFÉRENCE d'un compte prop firm est le profit compté pour
  // l'OBJECTIF, pas `totalPnl` : c'est lui qui décide si le trader valide, et
  // c'est lui qui coïncide avec le relevé de la prop firm. Les deux mesures
  // coexistent légitimement (cf. propFirmEngine.ts:214-235) ; ce qui manquait,
  // c'est de dire laquelle gouverne. `totalPnl` descend aux statistiques.
  const challengeProgress = (account as any).challengeProgress;
  const bufferStatus = (account as any).bufferStatus;
  const payoutProgress = (account as any).payoutProgress;
  const objectiveBase = (account as any).activeTier?.accountSize ?? account.initialBalance;

  const axisBounds = resolveBounds({
    status: account.status,
    initialBalance: account.initialBalance,
    objectiveBase,
    buffer: bufferStatus ?? null,
    challengeTargetAmount: challengeProgress?.targetAmount ?? null,
    payoutTargetAmount: payoutProgress?.targetAmount ?? null,
  });

  const axisTone: AxisTone = bufferStatus?.isViolation
    ? "violation"
    : bufferStatus?.isWarning
      ? "warning"
      : "normal";

  const isPropFirmAccount = !!(account as any).effectiveTemplate;
  // Compte perso : pas d'objectif, donc `totalPnl` reprend la vedette.
  const headlineProfit = isPropFirmAccount && challengeProgress
    ? challengeProgress.profit
    : account.totalPnl;

  const statusLabel = account.status === "challenge"
    ? t.tradingAccounts.statusChallenge
    : account.status === "pa"
    ? t.tradingAccounts.statusPa
    : account.status === "inactive"
    ? t.tradingAccounts.statusInactive
    : account.status === "demo"
    ? t.tradingAccounts.statusDemo
    : t.tradingAccounts.statusLive;

  return (
    <div className="w-full py-8 space-y-6">
      {/* Regles de la prop firm mises a jour : l'utilisateur decide */}
      {accountId && <RuleUpdateBanner accountId={accountId} />}

      {/* Compte prop firm sans aucune regle surveillee : la jauge vide se lit
          « tout va bien », pas « je ne mesure rien ». On rend le silence visible. */}
      {account && (
        <NoRulesBanner
          accounts={[{ id: account.id, name: account.name, hasNoRules: (account as any).hasNoRules }]}
        />
      )}

      {/* Header row */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/trading-accounts")}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.tradingAccounts.backToAccounts}
        </Button>
        <div className="flex items-center gap-2">
          {account.isActive ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeactivateConfirm(true)}
              className="gap-2"
            >
              <Power className="w-4 h-4" />
              {t.tradingAccounts.deactivateAccount}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReactivate}
              className="gap-2"
            >
              <Power className="w-4 h-4" />
              {t.tradingAccounts.activateAccount}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/trading-accounts?edit=${accountId}`)}
            className="gap-2"
          >
            <Pencil className="w-4 h-4" />
            {t.tradingAccounts.editAccount}
          </Button>
        </div>
      </div>

      {/* Reset suggéré par la sync : monté tout en haut car il conditionne la
          lecture de TOUTES les métriques de la page (un reset non confirmé
          laisse les pertes d'avant plomber le solde affiché). */}
      <AccountResetSuggestionBanner accountId={account.id} />

      {/* Deactivate confirm */}
      <Dialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.tradingAccounts.deactivateAccountConfirmTitle.replace("{name}", account.name)}</DialogTitle>
            <DialogDescription>
              {t.tradingAccounts.deactivateAccountConfirmDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateConfirm(false)}>{t.tradingAccounts.cancel}</Button>
            <Button onClick={handleConfirmDeactivate}>
              <Power className="h-4 w-4 mr-2" />
              {t.tradingAccounts.deactivate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromoteToFundedModal
        open={promoteModalOpen}
        onOpenChange={setPromoteModalOpen}
        accountId={account.id}
        initialBalance={account.initialBalance}
        trades={(account.trades ?? []).map((tr: any) => ({ date: typeof tr.date === "string" ? tr.date : new Date(tr.date).toISOString() }))}
        onPromoted={() => window.location.reload()}
      />

      {/* ÉTAGE 1 « où j'en suis » : une seule carte, un seul chiffre de
          référence, puis l'axe qui le situe entre son plancher et sa cible.
          C'est le MÊME axe que la card de la liste : la continuité visuelle
          entre les deux écrans passe par lui. */}
      <Card>
        <CardContent className="p-5 space-y-4">
          {/* Identité */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold truncate">{account.name}</h1>
                <Badge variant="outline" className="text-xs">{statusLabel}</Badge>
                <AccountSourceBadge account={account} />
                {account.effectiveTemplate && (
                  <Badge
                    className="text-xs font-medium"
                    style={{
                      backgroundColor: account.effectiveTemplate.color,
                      color: account.effectiveTemplate.textColor,
                    }}
                  >
                    {account.effectiveTemplate.name}
                  </Badge>
                )}
                {!account.isActive && (
                  <Badge variant="secondary" className="text-xs">{t.tradingAccounts.statusInactive}</Badge>
                )}
                {!account.isActive &&
                  (() => {
                    const badge = deactivationBadge(
                      account.deactivatedReason,
                      deactivationLabels(t),
                    );
                    if (!badge) return null;
                    return (
                      <Badge variant="secondary" className="text-xs" title={badge.hint}>
                        {badge.label}
                      </Badge>
                    );
                  })()}
                {bufferStatus?.isViolation && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-pnlNegative">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t.tradingAccounts.maxLossExceeded}
                  </span>
                )}
                <BalanceFreshnessHint
                  brokerBalance={(account as any).brokerBalance}
                  brokerBalanceAt={(account as any).brokerBalanceAt}
                />
              </div>
              <p className={`text-3xl font-bold font-mono ${headlineProfit >= 0 ? "text-pnlPositive" : "text-destructive"}`}>
                {headlineProfit >= 0 ? "+" : ""}{fmt(headlineProfit, CURRENCY)}
              </p>
              <p className="text-xs text-muted-foreground">
                {isPropFirmAccount ? t.tradingAccounts.profitTarget : t.tradingAccounts.tradesPnl}
              </p>
            </div>
            {canPromoteInPlace(account) && (
              <Button size="sm" onClick={() => setPromoteModalOpen(true)}>
                {t.tradingAccounts.promoteToFunded}
              </Button>
            )}
          </div>

          {/* L'axe, quand ses bornes sont résolues */}
          {axisBounds && (
            <AccountProgressBar
              bounds={axisBounds}
              currentBalance={account.currentBalance}
              currency={CURRENCY}
              tone={axisTone}
              size="lg"
            />
          )}

          {/* Jours tradés */}
          {challengeProgress && challengeProgress.daysTraded > 0 && (
            <p className="text-xs text-muted-foreground font-mono flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              {challengeProgress.minTradingDays !== null
                ? `${challengeProgress.daysTraded} / ${challengeProgress.minTradingDays} ${t.tradingAccounts.tradingDays.toLowerCase()}`
                : t.tradingAccounts.daysTradedCount.replace("{count}", String(challengeProgress.daysTraded))}
            </p>
          )}

          {/* Le solde de l'axe vient du broker, le PnL des trades : quand les
              deux divergent, le dire ici reste le seul endroit où l'utilisateur
              voit les deux chiffres côte à côte. */}
          <BalanceGapHint
            reconciliation={(account as any).balanceReconciliation}
            formatCurrency={(v) => fmt(v, CURRENCY)}
            className="text-[10px]"
          />
        </CardContent>
      </Card>

      {account.challengeRecap && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <span className="text-primary">●</span>
            {t.tradingAccounts.challengeRecapTitle}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeRecapFinalBalance}</p>
              <p className="font-mono font-semibold">{fmt(account.challengeRecap.finalBalance, CURRENCY)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeRecapNetProfit}</p>
              <p className="font-mono font-semibold text-pnlPositive">+{fmt(account.challengeRecap.netProfit, CURRENCY)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeRecapTradeCount}</p>
              <p className="font-mono font-semibold">{account.challengeRecap.tradeCount}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeRecapWinRate}</p>
              <p className="font-mono font-semibold">{account.challengeRecap.winRate.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeRecapDuration}</p>
              <p className="font-mono font-semibold">{t.tradingAccounts.challengeRecapDurationDays.replace("{days}", String(account.challengeRecap.durationDays))}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <motion.div
        key={metricsAnimKey}
        variants={metricsContainerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3"
      >
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.tradesCount}
            value={<AnimatedMetric value={account.tradesCount} format="integer" />}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.winRate}
            value={<AnimatedMetric value={account.winRate ?? 0} format="percent" suffix="%" />}
            sub={`${account.wins ?? 0}W / ${account.losses ?? 0}L`}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.avgWin}
            value={fmt(account.avgWin ?? 0, CURRENCY)}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.avgLoss}
            value={fmt(account.avgLoss ?? 0, CURRENCY)}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.profitFactor}
            value={account.profitFactor != null
              ? <AnimatedMetric value={account.profitFactor} format="decimal" />
              : "—"
            }
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.maxDrawdown}
            value={fmt(account.maxDrawdown ?? 0, CURRENCY)}
          />
        </motion.div>
        {/* Le PnL des trades a quitté l'en-tête au profit du chiffre de
            l'objectif : sans cette tuile il ne figurerait plus nulle part. */}
        <motion.div variants={metricsItemVariants}>
          <StatCard
            label={t.tradingAccounts.tradesPnl}
            value={
              <span className={account.totalPnl >= 0 ? "text-pnlPositive" : "text-destructive"}>
                {fmt(account.totalPnl, CURRENCY)}
              </span>
            }
            sub={fmtPct(variation)}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <motion.div
            animate={
              celebratePayout
                ? {
                    boxShadow: [
                      "0 0 0 0px hsl(var(--primary) / 0)",
                      "0 0 0 4px hsl(var(--primary) / 0.5)",
                      "0 0 0 0px hsl(var(--primary) / 0)",
                    ],
                  }
                : {}
            }
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="rounded-lg"
            ref={payoutCardRef}
          >
            <StatCard
              label={t.tradingAccounts.totalPayoutsReceived}
              value={
                <AnimatedMetric
                  value={account.totalPayouts ?? 0}
                  format="decimal"
                  decimals={2}
                  prefix="$"
                />
              }
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — charts + symbol stats */}
        <div className="lg:col-span-2 space-y-6">
          {/* Equity curve */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t.tradingAccounts.equityCurve}</CardTitle>
            </CardHeader>
            <CardContent>
              {account.equityCurve?.length ? (
                <>
                  <EquitySparkline
                    equityCurve={account.equityCurve}
                    initialBalance={account.initialBalance}
                    bufferStatus={account.bufferStatus}
                  />
                  <div className="mt-2 flex justify-between text-xs text-muted-foreground font-mono">
                    <span>{account.equityCurve[0]?.date}</span>
                    <span>{account.equityCurve[account.equityCurve.length - 1]?.date}</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {t.tradingAccounts.noEquityData}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Symbol stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t.tradingAccounts.symbolStats}</CardTitle>
            </CardHeader>
            <CardContent>
              <SymbolStatsTable
                symbolStats={account.symbolStats}
                currency={CURRENCY}
                t={t}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right column — propfirm rules + payouts */}
        <div className="space-y-6">
          <PropFirmSection account={account} t={t} />
          <PayoutsSection
            account={account}
            t={t}
            onPayoutAdded={handlePayoutAdded}
            onPayoutDeleted={handlePayoutDeleted}
          />
          {/* La card de la liste ne porte plus qu'une pastille d'alerte : l'état
              complet de la synchro Tradovate n'existe plus qu'ici. */}
          {(account as any).tradovateSyncedAccount?.connectionId && (
            <TradovateSyncStatus
              connectionId={(account as any).tradovateSyncedAccount.connectionId}
            />
          )}
        </div>
      </div>

      {/* MetaTrader connection section — not for Tradovate accounts */}
      {account.propFirm !== "tradovate" && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{t.metaApi.sectionTitle}</CardTitle>
                  {!METATRADER_SYNC_CONNECT_ENABLED && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      <Clock className="h-2.5 w-2.5" />
                      {t.common.soon}
                    </span>
                  )}
                </div>
                {METATRADER_SYNC_CONNECT_ENABLED && isPro && (
                  <Button size="sm" variant="outline" onClick={() => setShowConnectModal(true)}>
                    {t.metaApi.connect}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <MetaApiSyncStatus accountId={account.id} />
              {!METATRADER_SYNC_CONNECT_ENABLED && (
                <p className="text-sm text-muted-foreground mt-2">{t.metaApi.comingSoonNote}</p>
              )}
              {METATRADER_SYNC_CONNECT_ENABLED && !isPro && (
                <p className="text-sm text-muted-foreground mt-2">{t.metaApi.upsell.description}</p>
              )}
            </CardContent>
          </Card>

          <MetaApiConnectModal
            open={showConnectModal}
            accountId={account.id}
            onClose={() => setShowConnectModal(false)}
            onSuccess={() => refetch()}
          />
        </>
      )}

      {/* Challenge d'origine d'un compte funded (lien previousAccountId). */}
      <PredecessorLinkCard accountId={account.id} onChanged={refetch} />

      {/* Attachments section */}
      {accountId && <AttachmentsSection accountId={accountId} />}
    </div>
  );
}
